/**
 * Contratos de domínio do módulo COMPASSO (musicoterapia) — local-first, PT-BR.
 *
 * Espelha a gramática do módulo alimentar (src/types.ts), traduzida para o domínio
 * musical: "alimento" → "experiência musical", "passo de aproximação" → "passo de
 * engajamento". Reaproveita a entidade Criança e a tabela `foto` (Blob) do app.
 *
 * Decisões de arquitetura (vindas da spec docs/estudo-modulo-musicoterapia.html):
 *  - a `escada de engajamento` é um enum ORDENADO desde já (o número É o passo);
 *  - `origem` do momento é gravada SEMPRE (higiene de dados);
 *  - o `perfil sonoro` é dado estruturado, não texto livre;
 *  - regulação (acalmar↔ativar) é um eixo secundário leve e OPCIONAL.
 *
 * Em caso de dúvida, o código é a fonte da verdade.
 */
import type { Tendencia } from "../types";

// ──────────────────────────────────────────────────────────────────────────
// Escada de Engajamento Musical (o coração do módulo) — enum ORDENADO
// ──────────────────────────────────────────────────────────────────────────

/**
 * Progressão social-musical documentada na musicoterapia improvisacional
 * (Kim/Wigram/Gold; Nordoff-Robbins): tolerar o som → orientar-se → resposta
 * afetiva → produzir som → imitar → revezar → atenção conjunta → co-criar.
 * O valor numérico É o passo. Compare com < e >. Subir de 0 → 3 já é vitória.
 */
export enum PassoEngajamento {
  Presenca = 0, // está no ambiente sonoro, mesmo evitando
  Tolerancia = 1, // permanece perto do som; não se retira
  Orientacao = 2, // vira-se / olha para a fonte sonora
  RespostaAfetiva = 3, // reage com corpo ou expressão (balança, sorri, vocaliza)
  Exploracao = 4, // toca e manipula o instrumento / objeto sonoro
  ProducaoIntencional = 5, // faz som de propósito (percute, dedilha, canta)
  Imitacao = 6, // repete / responde a um padrão proposto
  Revezamento = 7, // troca musical em turnos (eu → você → eu)
  AtencaoCompartilhada = 8, // coordena olhar + som com o parceiro
  Cocriacao = 9, // propõe, lidera, improvisa junto — música como diálogo
}

/** Rótulos em sentence case para a UI. */
export const ENGAJAMENTO_LABEL: Record<PassoEngajamento, string> = {
  [PassoEngajamento.Presenca]: "presença com o som",
  [PassoEngajamento.Tolerancia]: "tolerância",
  [PassoEngajamento.Orientacao]: "orientação",
  [PassoEngajamento.RespostaAfetiva]: "resposta afetiva",
  [PassoEngajamento.Exploracao]: "exploração",
  [PassoEngajamento.ProducaoIntencional]: "produção intencional",
  [PassoEngajamento.Imitacao]: "imitação / eco",
  [PassoEngajamento.Revezamento]: "revezamento",
  [PassoEngajamento.AtencaoCompartilhada]: "atenção compartilhada",
  [PassoEngajamento.Cocriacao]: "co-criação / iniciativa",
};

/** Descrição curta de cada passo (ajuda o cuidador a marcar com confiança). */
export const ENGAJAMENTO_DESC: Record<PassoEngajamento, string> = {
  [PassoEngajamento.Presenca]: "está no ambiente sonoro, mesmo evitando",
  [PassoEngajamento.Tolerancia]: "permanece perto do som; não se retira",
  [PassoEngajamento.Orientacao]: "vira-se ou olha para a fonte do som",
  [PassoEngajamento.RespostaAfetiva]: "reage: balança, sorri, vocaliza, aproxima-se",
  [PassoEngajamento.Exploracao]: "toca e manipula o instrumento ou objeto",
  [PassoEngajamento.ProducaoIntencional]: "faz som de propósito (percute, canta)",
  [PassoEngajamento.Imitacao]: "repete ou responde a um padrão proposto",
  [PassoEngajamento.Revezamento]: "troca musical em turnos: eu → você → eu",
  [PassoEngajamento.AtencaoCompartilhada]: "coordena olhar e som com o parceiro",
  [PassoEngajamento.Cocriacao]: "propõe, lidera, improvisa junto",
};

export const ENGAJAMENTO_MIN = PassoEngajamento.Presenca; // 0
export const ENGAJAMENTO_MAX = PassoEngajamento.Cocriacao; // 9

// ──────────────────────────────────────────────────────────────────────────
// Enums / unions da experiência
// ──────────────────────────────────────────────────────────────────────────

/** Natureza da experiência musical. */
export type TipoExperiencia =
  | "cancao"
  | "instrumento"
  | "improviso"
  | "playlist"
  | "atividade";

export const TIPO_LABEL: Record<TipoExperiencia, string> = {
  cancao: "canção",
  instrumento: "instrumento",
  improviso: "improviso",
  playlist: "playlist",
  atividade: "atividade",
};

export const TIPOS_ORDEM: TipoExperiencia[] = [
  "cancao",
  "instrumento",
  "improviso",
  "playlist",
  "atividade",
];

/** Objetivo pretendido da experiência (didático — não trava nada). */
export type FuncaoMusical = "conectar" | "comunicar" | "regular" | "ativar" | "motor";

export const FUNCAO_LABEL: Record<FuncaoMusical, string> = {
  conectar: "conectar",
  comunicar: "comunicar",
  regular: "acalmar / regular",
  ativar: "ativar / energizar",
  motor: "movimento",
};

/** Como a criança participa (pode ser >1). */
export type Modalidade = "ouvir" | "tocar" | "cantar" | "mover";

export const MODALIDADE_LABEL: Record<Modalidade, string> = {
  ouvir: "ouvir",
  tocar: "tocar",
  cantar: "cantar",
  mover: "mover",
};

export const MODALIDADES_ORDEM: Modalidade[] = ["ouvir", "tocar", "cantar", "mover"];

// Dimensões do perfil sonoro (derivadas dos mecanismos neurológicos da §3 da spec).
export type Andamento = "lento" | "moderado" | "rapido";
export type Intensidade = "suave" | "media" | "forte";
export type Previsibilidade = "alta" | "media" | "baixa";
export type Familiaridade = "nova" | "conhecida" | "favorita";

export const ANDAMENTO_LABEL: Record<Andamento, string> = {
  lento: "lento",
  moderado: "moderado",
  rapido: "rápido",
};
export const INTENSIDADE_LABEL: Record<Intensidade, string> = {
  suave: "suave",
  media: "média",
  forte: "forte",
};
export const PREVISIBILIDADE_LABEL: Record<Previsibilidade, string> = {
  alta: "alta",
  media: "média",
  baixa: "baixa",
};
export const FAMILIARIDADE_LABEL: Record<Familiaridade, string> = {
  nova: "nova",
  conhecida: "conhecida",
  favorita: "favorita",
};

/**
 * Perfil sonoro — 5 dimensões estruturadas. Base para "experiências parecidas"
 * (P1) e para explicar POR QUE uma experiência funciona.
 */
export interface PerfilSonoro {
  andamento: Andamento | null;
  intensidade: Intensidade | null;
  timbre: string | null; // livre / tags (ex.: "metálico", "voz", "madeira")
  previsibilidade: Previsibilidade | null;
  familiaridade: Familiaridade | null;
}

/** Status da experiência. NÃO é nota — é situação. Nunca renderize como erro/vermelho. */
export type StatusExperiencia = "favorita" | "em_exploracao" | "sensivel" | "nao_usada";

export const STATUS_EXPERIENCIA_LABEL: Record<StatusExperiencia, string> = {
  favorita: "favorita",
  em_exploracao: "em exploração",
  sensivel: "sensível",
  nao_usada: "não usada",
};

// ──────────────────────────────────────────────────────────────────────────
// Eixo de regulação (acalmar ↔ ativar) — opcional, leve
// ──────────────────────────────────────────────────────────────────────────

/** Estado de ativação (arousal) antes/depois do momento — escala simétrica de 3. */
export type EstadoRegulacao = "desregulado_baixo" | "regulado" | "desregulado_cima";

export const ESTADO_REGULACAO_LABEL: Record<EstadoRegulacao, string> = {
  desregulado_baixo: "apático / baixo",
  regulado: "regulado",
  desregulado_cima: "agitado / alto",
};

/** Efeito regulatório DERIVADO de uma experiência (estado-antes vs estado-depois). */
export type EfeitoRegulatorio = "acalma" | "ativa" | "regula" | "variavel" | "poucos_dados";

export const EFEITO_LABEL: Record<EfeitoRegulatorio, string> = {
  acalma: "tende a acalmar",
  ativa: "tende a ativar",
  regula: "tende a regular",
  variavel: "variável",
  poucos_dados: "poucos dados",
};

// ──────────────────────────────────────────────────────────────────────────
// Contexto / origem do momento
// ──────────────────────────────────────────────────────────────────────────

/** Quem originou o momento. Gravado automaticamente (higiene de dados). */
export type OrigemMomento = "cuidador" | "crianca";

export const ORIGEM_MOMENTO_LABEL: Record<OrigemMomento, string> = {
  cuidador: "cuidador",
  crianca: "criança",
};

/** Contexto do momento musical (opcional). */
export type ContextoMomento =
  | "transicao"
  | "banho"
  | "sono"
  | "brincadeira"
  | "refeicao"
  | "escola"
  | "outro";

export const CONTEXTO_LABEL: Record<ContextoMomento, string> = {
  transicao: "transição",
  banho: "banho",
  sono: "sono",
  brincadeira: "brincadeira",
  refeicao: "refeição",
  escola: "escola",
  outro: "outro",
};

export const CONTEXTOS_ORDEM: ContextoMomento[] = [
  "transicao",
  "banho",
  "sono",
  "brincadeira",
  "refeicao",
  "escola",
  "outro",
];

// ──────────────────────────────────────────────────────────────────────────
// Entidades
// ──────────────────────────────────────────────────────────────────────────

/** Experiência musical (biblioteca) — rica, definida 1×. */
export interface ExperienciaMusical {
  id: string;
  nome: string;
  tipo: TipoExperiencia;
  midias: string[]; // refs locais (foto do instrumento / clipe de áudio); a 1ª é a capa
  funcao: FuncaoMusical | null;
  sonoro: PerfilSonoro;
  modalidade: Modalidade[]; // como a criança participa (pode ser >1)
  status: StatusExperiencia;
  observacoes: string | null;
  arquivado: boolean; // arquivar em vez de deletar
  criado_em: string; // ISO 8601
}

/**
 * Momento musical (evento) — mínimo, recorrente, ≤10s. Só experiencia_id,
 * crianca_id, data_hora, passo_engajamento e origem são necessários para salvar.
 * Todo o resto é opcional e NUNCA bloqueia.
 */
export interface MomentoMusical {
  id: string;
  experiencia_id: string;
  crianca_id: string;
  data_hora: string; // ISO 8601
  passo_engajamento: PassoEngajamento; // 0–9 (obrigatório — é a "reação")
  estado_antes: EstadoRegulacao | null; // opcional — eixo de regulação
  estado_depois: EstadoRegulacao | null; // opcional — eixo de regulação
  duracao_min: number | null; // opcional
  contexto: ContextoMomento | null; // opcional
  com_quem: string[]; // pode ser []
  origem: OrigemMomento; // gravado automaticamente
  confirmado: boolean; // origem "crianca" nasce pendente (false)
  notas: string | null;
}

// ──────────────────────────────────────────────────────────────────────────
// Derivados por experiência (CALCULADOS, não persistidos)
// ──────────────────────────────────────────────────────────────────────────

export interface DerivadosExperiencia {
  experiencia_id: string;
  n_momentos: number;
  ultimo_momento: string | null; // ISO 8601
  passo_atual: PassoEngajamento | null; // passo do momento mais recente
  passo_maximo: PassoEngajamento | null; // maior passo de engajamento já atingido
  tendencia: Tendencia;
  efeito_regulatorio: EfeitoRegulatorio;
  dias_desde_ultimo: number | null; // alimenta o lembrete de rotina (P0.6)
}
